import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  InputBase,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Category as CategoryIcon,
  SwapHoriz as SwapHorizIcon,
  ListAlt as ListAltIcon,
  Description as DescriptionIcon,
  QuestionAnswer as ConsultasIcon,
  Assessment as AssessmentIcon,
  AutoGraph as AutoGraphIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as DisponibilidadesIcon,
  Timeline as FlujoFondosIcon,
  Straighten as StraightenIcon,
  VpnKey as VpnKeyIcon,
  GppGood as GppGoodIcon,
  Http as WebhookIcon,
  AccountTree as AccountTreeIcon,
  UploadFile as UploadFileIcon,
  PriceChange as PriceChangeIcon,
  Schema as SchemaIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Engineering as EngineeringIcon,
  DirectionsCar as DirectionsCarIcon,
  EventAvailable as EventAvailableIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  BuildCircle as BuildCircleIcon,
  Science as ScienceIcon,
  Factory as FactoryIcon,
  Checkroom as FashionIcon,
  Palette as PaletteIcon,
  Style as ModeloIcon,
  WbSunny as TemporadaIcon,
  Contacts as ContactsIcon,
  Business as BusinessIcon,
  ViewKanban as ViewKanbanIcon,
  Inventory as InventarioIcon,
  History as HistoryIcon,
  Percent as PercentIcon,
  North as NorthIcon,
  South as SouthIcon,
  QrCodeScanner as QrCodeScannerIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { canManageSistemaUsers, effectiveUserRoles } from '../utils/sistemaAuthz';
import { administrationMenuItem, isTesoreriaAdminHubPath } from '../config/adminNavigation';
import { clearAdminBackTo, navigateWithAdminBack } from '../utils/adminNavigationContext';
import {
  buildMenuVisualByPath,
  resolveMenuVisual,
} from '../config/navigationMenu';

interface AccesoDirecto {
  text: string;
  icon: React.ReactNode;
  path: string;
  categoria: string;
  grupo: string;
  /** Color del cuadrado del ícono (misma paleta que el menú del módulo). */
  accent?: string;
}

interface BuscadorAccesosModalProps {
  open: boolean;
  onClose: () => void;
}

const RECIENTES_KEY = 'eone.buscador.recientes';
const RECIENTES_MAX = 6;

/** Normaliza texto para búsqueda sin acentos: "línea" y "linea" coinciden */
const normalizeForSearch = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const ordenCategorias = [
  'General',
  'Ventas',
  'Compras',
  'Inventario',
  'Tesorería',
  'CRM',
  'Servicio',
  'Producción',
  'Fashion',
  'Contabilidad',
  'Configuraciones',
  'Sistema',
];

const categoriaAccent: Record<string, string> = {
  General: '#7b1fa2',
  Ventas: '#1976d2',
  Compras: '#00838f',
  Inventario: '#2e7d32',
  Tesorería: '#ed6c02',
  CRM: '#0288d1',
  Servicio: '#5e35b1',
  Producción: '#455a64',
  Fashion: '#c2185b',
  Contabilidad: '#1565c0',
  Configuraciones: '#6d4c41',
  Sistema: '#546e7a',
  Recientes: '#607d8b',
};

const loadRecientes = (): string[] => {
  try {
    const raw = localStorage.getItem(RECIENTES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
};

const saveReciente = (path: string) => {
  const prev = loadRecientes().filter((p) => p !== path);
  localStorage.setItem(RECIENTES_KEY, JSON.stringify([path, ...prev].slice(0, RECIENTES_MAX)));
};

const KbdHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 22,
      height: 20,
      px: 0.6,
      borderRadius: 0.75,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: alpha('#000', 0.03),
      fontSize: '0.65rem',
      fontWeight: 600,
      color: 'text.secondary',
      fontFamily: 'inherit',
      lineHeight: 1,
    }}
  >
    {children}
  </Box>
);

const BuscadorAccesosModal: React.FC<BuscadorAccesosModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const { canPage } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recientesPaths, setRecientesPaths] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const menuVisuals = useMemo(() => buildMenuVisualByPath(), []);

  const withMenuVisual = useCallback(
    (acceso: AccesoDirecto): AccesoDirecto => {
      const visual = resolveMenuVisual(acceso.path, menuVisuals);
      if (!visual) return acceso;
      return {
        ...acceso,
        icon: visual.icon,
        accent: visual.color,
      };
    },
    [menuVisuals]
  );

  const todosLosAccesos: AccesoDirecto[] = useMemo(
    () =>
      [
      { text: 'Indicadores', icon: <DashboardIcon />, path: '/dashboard', categoria: 'General', grupo: 'Dashboard' },
      { text: 'Consulta IA Ventas', icon: <ConsultasIcon />, path: '/ventas/consultas-ia', categoria: 'Ventas', grupo: 'Consultas' },
      { text: 'Consulta IA Compras', icon: <ConsultasIcon />, path: '/compras/consultas-ia', categoria: 'Compras', grupo: 'Consultas' },
      { text: 'Consulta IA Inventario', icon: <ConsultasIcon />, path: '/inventario/consultas-ia', categoria: 'Inventario', grupo: 'Consultas' },
      { text: 'Consulta IA Tesorería', icon: <ConsultasIcon />, path: '/tesoreria/consultas-ia', categoria: 'Tesorería', grupo: 'Consultas' },

      { text: 'Cotizaciones', icon: <ListAltIcon />, path: '/ventas/cotizaciones', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Pedidos', icon: <ListAltIcon />, path: '/ventas/pedidos', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Picking Masivo', icon: <QrCodeScannerIcon />, path: '/ventas/picking-masivo', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Contratos', icon: <DescriptionIcon />, path: '/ventas/contratos', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Facturación de contratos', icon: <ReceiptIcon />, path: '/ventas/contratos/facturacion', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Facturas', icon: <ListAltIcon />, path: '/ventas/facturas', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Remitos', icon: <ListAltIcon />, path: '/ventas/remitos', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Notas de Crédito', icon: <ListAltIcon />, path: '/ventas/notas-credito', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Notas de Débito', icon: <ListAltIcon />, path: '/ventas/notas-debito', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Planes de Visitas', icon: <ListAltIcon />, path: '/ventas/visitas?tab=planes', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Visitas (Ejecución)', icon: <ListAltIcon />, path: '/ventas/visitas?tab=visitas', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Informes de Ventas', icon: <AssessmentIcon />, path: '/ventas/informes', categoria: 'Ventas', grupo: 'Informes' },
      { text: 'Cuentas por Cobrar', icon: <AssessmentIcon />, path: '/ventas/informes/cuentas-cobrar', categoria: 'Ventas', grupo: 'Informes' },
      { text: 'Resumen de Cuenta', icon: <AssessmentIcon />, path: '/ventas/informes/resumen-cuenta', categoria: 'Ventas', grupo: 'Informes' },
      { text: 'Libro IVA Ventas', icon: <AssessmentIcon />, path: '/ventas/informes/libro-iva-ventas', categoria: 'Ventas', grupo: 'Informes' },
      { text: 'Informe dinámico de ventas', icon: <AssessmentIcon />, path: '/ventas/informes/ventas-dinamico', categoria: 'Ventas', grupo: 'Informes' },
      { text: 'Ventas por producto', icon: <AssessmentIcon />, path: '/ventas/informes/ventas-por-producto', categoria: 'Ventas', grupo: 'Informes' },
      { text: 'Resultados de ventas', icon: <AssessmentIcon />, path: '/ventas/informes/ventas-resultados', categoria: 'Ventas', grupo: 'Informes' },
      { text: 'Clientes', icon: <CategoryIcon />, path: '/ventas/clientes', categoria: 'Ventas', grupo: 'Configuraciones' },
      { text: 'Vendedores', icon: <CategoryIcon />, path: '/ventas/vendedores', categoria: 'Ventas', grupo: 'Configuraciones' },
      { text: 'Liquidación de comisiones', icon: <PercentIcon />, path: '/ventas/liquidacion-comisiones', categoria: 'Ventas', grupo: 'Procesos' },
      { text: 'Rutas', icon: <CategoryIcon />, path: '/ventas/rutas', categoria: 'Ventas', grupo: 'Configuraciones' },
      { text: 'Listas de Precios', icon: <CategoryIcon />, path: '/ventas/listaprecios', categoria: 'Ventas', grupo: 'Configuraciones' },
      { text: 'Artículos por Lista de Precio', icon: <CategoryIcon />, path: '/ventas/articuloxlistaprecio', categoria: 'Ventas', grupo: 'Configuraciones' },
      { text: 'Cotizaciones de moneda', icon: <CategoryIcon />, path: '/sistema/movmoneda', categoria: 'Ventas', grupo: 'Configuraciones' },

      { text: 'Órdenes de Compra', icon: <ListAltIcon />, path: '/compras/ordenes', categoria: 'Compras', grupo: 'Procesos' },
      { text: 'Remitos de Compra', icon: <ListAltIcon />, path: '/compras/remitos', categoria: 'Compras', grupo: 'Procesos' },
      { text: 'Facturas de Compra', icon: <ReceiptIcon />, path: '/compras/facturas', categoria: 'Compras', grupo: 'Procesos' },
      { text: 'Notas de Crédito', icon: <ListAltIcon />, path: '/compras/notas-credito', categoria: 'Compras', grupo: 'Procesos' },
      { text: 'Informes de Compras', icon: <AssessmentIcon />, path: '/compras/informes', categoria: 'Compras', grupo: 'Informes' },
      { text: 'Cuentas por Pagar', icon: <AssessmentIcon />, path: '/compras/informes/cuentas-pagar', categoria: 'Compras', grupo: 'Informes' },
      { text: 'Resumen de Cuenta', icon: <AssessmentIcon />, path: '/compras/informes/resumen-cuenta', categoria: 'Compras', grupo: 'Informes' },
      { text: 'Libro IVA Compras', icon: <AssessmentIcon />, path: '/compras/informes/libro-iva-compras', categoria: 'Compras', grupo: 'Informes' },
      { text: 'Sugerencia de compra', icon: <AutoGraphIcon />, path: '/compras/informes/sugerencia-compra', categoria: 'Compras', grupo: 'Informes' },
      { text: 'Proveedores', icon: <CategoryIcon />, path: '/compras/proveedores', categoria: 'Compras', grupo: 'Configuraciones' },
      { text: 'Ramos de Proveedores', icon: <CategoryIcon />, path: '/compras/ramosprov', categoria: 'Compras', grupo: 'Configuraciones' },
      { text: 'Cotizaciones de moneda', icon: <CategoryIcon />, path: '/sistema/movmoneda', categoria: 'Compras', grupo: 'Configuraciones' },

      { text: 'Ajustes y Transferencias', icon: <SwapHorizIcon />, path: '/inventario/transferencias', categoria: 'Inventario', grupo: 'Procesos' },
      { text: 'Conteo de Stock', icon: <InventarioIcon />, path: '/inventario/conteo', categoria: 'Inventario', grupo: 'Procesos' },
      { text: 'Mantenimiento de precios', icon: <PriceChangeIcon />, path: '/inventario/mantenimiento-precios', categoria: 'Inventario', grupo: 'Procesos' },
      { text: 'Ofertas', icon: <PriceChangeIcon />, path: '/inventario/descuentos', categoria: 'Inventario', grupo: 'Configuraciones' },
      { text: 'Importación costos Base por código de proveedor', icon: <UploadFileIcon />, path: '/inventario/importacion-costos-proveedor', categoria: 'Inventario', grupo: 'Procesos' },
      { text: 'Importación costos Base por código de artículo', icon: <UploadFileIcon />, path: '/inventario/importacion-costos-articulo', categoria: 'Inventario', grupo: 'Procesos' },
      { text: 'Informes de Inventario', icon: <AssessmentIcon />, path: '/inventario/informes', categoria: 'Inventario', grupo: 'Informes' },
      { text: 'Stock por Depósito', icon: <AssessmentIcon />, path: '/inventario/informes/stock-deposito', categoria: 'Inventario', grupo: 'Informes' },
      { text: 'Movimientos de Stock', icon: <AssessmentIcon />, path: '/inventario/informes/movimientos-stock', categoria: 'Inventario', grupo: 'Informes' },
      { text: 'Productos', icon: <CategoryIcon />, path: '/inventario/productos', categoria: 'Inventario', grupo: 'Configuraciones' },
      { text: 'Ramos', icon: <CategoryIcon />, path: '/inventario/ramos', categoria: 'Inventario', grupo: 'Configuraciones' },
      { text: 'Líneas', icon: <CategoryIcon />, path: '/inventario/lineas', categoria: 'Inventario', grupo: 'Configuraciones' },
      { text: 'Clases', icon: <CategoryIcon />, path: '/inventario/clases', categoria: 'Inventario', grupo: 'Configuraciones' },
      { text: 'Unidades de medida', icon: <StraightenIcon />, path: '/inventario/unidades-medida', categoria: 'Inventario', grupo: 'Configuraciones' },

      { text: 'Recibos', icon: <ListAltIcon />, path: '/tesoreria/recibos', categoria: 'Tesorería', grupo: 'Procesos' },
      { text: 'Orden de Pago', icon: <ListAltIcon />, path: '/tesoreria/orden-pago', categoria: 'Tesorería', grupo: 'Procesos' },
      { text: 'Traspasos', icon: <SwapHorizIcon />, path: '/tesoreria/transferencias', categoria: 'Tesorería', grupo: 'Procesos' },
      { text: 'Movimientos Varios', icon: <ListAltIcon />, path: '/tesoreria/movimientos-varios', categoria: 'Tesorería', grupo: 'Procesos' },
      { text: 'Movimientos Bancarios', icon: <ListAltIcon />, path: '/tesoreria/movimientos-bancarios', categoria: 'Tesorería', grupo: 'Procesos' },
      { text: 'Conciliación Bancaria', icon: <ListAltIcon />, path: '/tesoreria/conciliacion-bancaria', categoria: 'Tesorería', grupo: 'Procesos' },
      { text: 'Planilla de Caja', icon: <ReceiptIcon />, path: '/tesoreria/movimientos-caja', categoria: 'Tesorería', grupo: 'Procesos' },
      { text: 'Informes de Tesorería', icon: <AssessmentIcon />, path: '/tesoreria/informes', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Flujo de Fondos', icon: <FlujoFondosIcon />, path: '/tesoreria/informes/flujo-fondos', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Disponibilidades', icon: <DisponibilidadesIcon />, path: '/tesoreria/disponibilidades', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Movimientos de Carteras', icon: <AssessmentIcon />, path: '/tesoreria/informes/movimientos-carteras', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Cheques de Tercero', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-tercero', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Cheques de Terceros en Cartera', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-terceros-cartera', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Cheques Propios', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-propios', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Consulta de Cheques Propios (Acreditar)', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-propios-pendientes-acreditar', categoria: 'Tesorería', grupo: 'Informes' },
      { text: 'Bancos', icon: <CategoryIcon />, path: '/tesoreria/bancos', categoria: 'Tesorería', grupo: 'Configuraciones' },
      { text: 'Carteras', icon: <CategoryIcon />, path: '/tesoreria/carteras', categoria: 'Tesorería', grupo: 'Configuraciones' },
      { text: 'Grupos de Caja', icon: <CategoryIcon />, path: '/tesoreria/grupos-mcp', categoria: 'Tesorería', grupo: 'Configuraciones' },
      { text: 'Medios MCP', icon: <CategoryIcon />, path: '/tesoreria/medios-mcp', categoria: 'Tesorería', grupo: 'Configuraciones' },
      { text: 'Tarjetas', icon: <CategoryIcon />, path: '/tesoreria/tarjetas', categoria: 'Tesorería', grupo: 'Configuraciones' },

      { text: 'CRM', icon: <ContactsIcon />, path: '/crm', categoria: 'CRM', grupo: 'Módulo' },
      { text: 'Contactos', icon: <ContactsIcon />, path: '/crm/contactos', categoria: 'CRM', grupo: 'Operaciones' },
      { text: 'Empresas', icon: <BusinessIcon />, path: '/crm/empresas', categoria: 'CRM', grupo: 'Operaciones' },
      { text: 'Pipeline', icon: <ViewKanbanIcon />, path: '/crm/pipeline', categoria: 'CRM', grupo: 'Operaciones' },
      { text: 'KPIs', icon: <AssessmentIcon />, path: '/crm/kpis', categoria: 'CRM', grupo: 'Informes' },
      { text: 'Configuración CRM', icon: <SettingsIcon />, path: '/crm/config', categoria: 'CRM', grupo: 'Configuraciones' },

      { text: 'Administración', icon: <SettingsIcon />, path: '/configuraciones', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Usuarios', icon: <CategoryIcon />, path: '/sistema/usuarios', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Envio de Correos', icon: <EmailIcon />, path: '/configuraciones/correo-salida', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Parámetros del sistema', icon: <SettingsIcon />, path: '/configuraciones/parametros', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Variables del sistema', icon: <SettingsIcon />, path: '/configuraciones/variables-sistema', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Apariencia de la empresa', icon: <SettingsIcon />, path: '/configuraciones/apariencia', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Tipos de Formulario', icon: <CategoryIcon />, path: '/sistema/tipo-formulario', categoria: 'Sistema', grupo: 'Configuraciones' },
      { text: 'Tipos de formulario por Sucursal', icon: <CategoryIcon />, path: '/configuraciones/sucventa', categoria: 'Sistema', grupo: 'Configuraciones' },
      { text: 'Sucursales de Venta', icon: <CategoryIcon />, path: '/configuraciones/sucursalesventa', categoria: 'Sistema', grupo: 'Configuraciones' },
      { text: 'Compañías', icon: <CategoryIcon />, path: '/sistema/companias', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Certificados ARCA', icon: <GppGoodIcon />, path: '/sistema/certificados-arca', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Tipos de comprobante ARCA', icon: <CategoryIcon />, path: '/sistema/wsfe-tipcomp', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Administración de Tenants', icon: <CategoryIcon />, path: '/sistema/tenants', categoria: 'Sistema', grupo: 'Configuraciones' },
      { text: 'Caja por Usuario', icon: <CategoryIcon />, path: '/sistema/caja-usuario', categoria: 'Sistema', grupo: 'Configuraciones' },
      { text: 'Grupos de Caja por Usuario', icon: <CategoryIcon />, path: '/sistema/grupo-caja-usuario', categoria: 'Sistema', grupo: 'Configuraciones' },

      { text: 'Monedas', icon: <CategoryIcon />, path: '/sistema/monedas', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Cotizaciones de moneda', icon: <CategoryIcon />, path: '/sistema/movmoneda', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Provincias', icon: <CategoryIcon />, path: '/sistema/provincias', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Localidades', icon: <CategoryIcon />, path: '/sistema/localidades', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Transportes', icon: <CategoryIcon />, path: '/configuraciones/transportes', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Impuestos', icon: <CategoryIcon />, path: '/configuraciones/impuestos', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Ubicaciones', icon: <CategoryIcon />, path: '/configuraciones/ubicaciones', categoria: 'Configuraciones', grupo: 'Configuraciones' },
      { text: 'Tokens de integración', icon: <VpnKeyIcon />, path: '/configuraciones/tokens-integracion', categoria: 'Configuraciones', grupo: 'Integraciones' },
      { text: 'Claves de agente (LLM)', icon: <VpnKeyIcon />, path: '/configuraciones/claves-agente', categoria: 'Configuraciones', grupo: 'Integraciones' },
      { text: 'Webhooks', icon: <WebhookIcon />, path: '/configuraciones/webhooks', categoria: 'Configuraciones', grupo: 'Integraciones' },

      { text: 'Asientos / Libro diario', icon: <ListAltIcon />, path: '/contabilidad/asientos-diario', categoria: 'Contabilidad', grupo: 'Operaciones' },
      { text: 'Libro mayor', icon: <AccountTreeIcon />, path: '/contabilidad/libro-mayor', categoria: 'Contabilidad', grupo: 'Operaciones' },
      { text: 'Plan de cuentas', icon: <SchemaIcon />, path: '/contabilidad/plan-cuentas', categoria: 'Contabilidad', grupo: 'Operaciones' },
      { text: 'Informes', icon: <AssessmentIcon />, path: '/contabilidad/informes', categoria: 'Contabilidad', grupo: 'Informes' },
      { text: 'Estado de resultados', icon: <AssessmentIcon />, path: '/contabilidad/estado-resultados', categoria: 'Contabilidad', grupo: 'Informes' },
      { text: 'Situación patrimonial', icon: <AssessmentIcon />, path: '/contabilidad/situacion-patrimonial', categoria: 'Contabilidad', grupo: 'Informes' },
      { text: 'Estrategias', icon: <CategoryIcon />, path: '/contabilidad/estrategia', categoria: 'Contabilidad', grupo: 'Configuraciones' },

      { text: 'Técnicos', icon: <EngineeringIcon />, path: '/servicio/tecnicos', categoria: 'Servicio', grupo: 'Configuraciones' },
      { text: 'Unidades', icon: <DirectionsCarIcon />, path: '/servicio/vehiculos', categoria: 'Servicio', grupo: 'Configuraciones' },
      { text: 'Turnos', icon: <EventAvailableIcon />, path: '/servicio/turnos', categoria: 'Servicio', grupo: 'Operaciones' },
      { text: 'Organizador', icon: <CalendarViewWeekIcon />, path: '/servicio/organizador', categoria: 'Servicio', grupo: 'Operaciones' },
      { text: 'Órdenes de trabajo', icon: <BuildCircleIcon />, path: '/servicio/ordenes-reparacion', categoria: 'Servicio', grupo: 'Operaciones' },

      { text: 'Recetas', icon: <ScienceIcon />, path: '/produccion/recetas', categoria: 'Producción', grupo: 'Maestros' },
      { text: 'Órdenes de producción', icon: <FactoryIcon />, path: '/produccion/ordenes', categoria: 'Producción', grupo: 'Operaciones' },
      { text: 'Modelos', icon: <ModeloIcon />, path: '/inventario/fashion/modelos', categoria: 'Fashion', grupo: 'Maestros' },
      { text: 'Colores', icon: <PaletteIcon />, path: '/inventario/fashion/colores', categoria: 'Fashion', grupo: 'Maestros' },
      { text: 'Curvas de talle', icon: <StraightenIcon />, path: '/inventario/fashion/curvas-talle', categoria: 'Fashion', grupo: 'Maestros' },
      { text: 'Temporadas', icon: <TemporadaIcon />, path: '/inventario/fashion/temporadas', categoria: 'Fashion', grupo: 'Maestros' },
      { text: 'Curva de talles (informe)', icon: <FashionIcon />, path: '/inventario/informes/curva-talles', categoria: 'Fashion', grupo: 'Informes' },
    ].map(withMenuVisual),
    [withMenuVisual]
  );

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedIndex(0);
      setRecientesPaths(loadRecientes());
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  const accesosDisponibles = useMemo(() => {
    const roles = effectiveUserRoles(user?.roles);
    const isSuperAdmin = roles.some((r) => String(r).trim().toUpperCase() === 'SUPERADMIN');
    const canManageUsers = canManageSistemaUsers(roles);

    let accesos = todosLosAccesos;

    if (!isSuperAdmin) {
      accesos = accesos.filter((acceso) => acceso.categoria !== 'Sistema');
    }

    if (!isSuperAdmin && !canManageUsers) {
      accesos = accesos.filter((acceso) => acceso.categoria !== 'Configuraciones');
    }

    const normalizedRoles = (user?.roles || []).map((r) => String(r).trim().toLowerCase());
    const canManageVisitas = normalizedRoles.some((r) =>
      ['admin', 'superadmin', 'gerente'].includes(r)
    );
    if (!canManageVisitas) {
      accesos = accesos.filter((acceso) => !acceso.path.startsWith('/ventas/visitas'));
    }

    accesos = accesos.filter((acceso) => {
      if (acceso.categoria === 'Sistema' || acceso.categoria === 'Configuraciones') return true;
      return canPage(acceso.path);
    });

    return accesos;
  }, [todosLosAccesos, user?.roles, canPage]);

  const accesosFiltrados = useMemo(() => {
    if (!searchTerm.trim()) {
      return accesosDisponibles;
    }

    const termino = normalizeForSearch(searchTerm.trim());
    return accesosDisponibles.filter(
      (acceso) =>
        normalizeForSearch(acceso.text).includes(termino) ||
        normalizeForSearch(acceso.categoria).includes(termino) ||
        normalizeForSearch(acceso.grupo).includes(termino) ||
        normalizeForSearch(acceso.path).includes(termino)
    );
  }, [searchTerm, accesosDisponibles]);

  const recientes = useMemo(() => {
    if (searchTerm.trim()) return [];
    return recientesPaths
      .map((path) => accesosDisponibles.find((a) => a.path === path))
      .filter((a): a is AccesoDirecto => Boolean(a));
  }, [recientesPaths, accesosDisponibles, searchTerm]);

  type FlatItem = { acceso: AccesoDirecto; section: string };

  const flatItems = useMemo((): FlatItem[] => {
    const items: FlatItem[] = [];
    const recientesPathsSet = new Set(recientes.map((r) => r.path));

    if (recientes.length > 0) {
      recientes.forEach((acceso) => {
        items.push({ acceso, section: 'Recientes' });
      });
    }

    ordenCategorias.forEach((categoria) => {
      const deCategoria = accesosFiltrados.filter((a) => a.categoria === categoria);
      // Sin búsqueda: no repetir los que ya están en Recientes
      const list =
        !searchTerm.trim() && recientesPathsSet.size > 0
          ? deCategoria.filter((a) => !recientesPathsSet.has(a.path))
          : deCategoria;
      list.forEach((acceso) => {
        items.push({ acceso, section: categoria });
      });
    });

    return items;
  }, [accesosFiltrados, recientes, searchTerm]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm, flatItems.length]);

  useEffect(() => {
    const el = itemRefs.current[selectedIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleAccesoClick = useCallback(
    (acceso: AccesoDirecto) => {
      saveReciente(acceso.path);

      const desdeAdministracion =
        acceso.grupo === 'Configuraciones' && acceso.categoria !== 'Tesorería';

      if (desdeAdministracion) {
        navigateWithAdminBack(navigate, acceso.path, administrationMenuItem.path);
      } else if (isTesoreriaAdminHubPath(acceso.path)) {
        clearAdminBackTo();
        navigate(acceso.path);
      } else {
        navigate(acceso.path);
      }
      onClose();
    },
    [navigate, onClose]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, Math.max(flatItems.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = flatItems[selectedIndex];
      if (item) handleAccesoClick(item.acceso);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      onKeyDown={handleKeyDown}
      BackdropProps={{
        sx: {
          backgroundColor: alpha('#0f172a', 0.35),
          backdropFilter: 'blur(2px)',
        },
      }}
      PaperProps={{
        sx: {
          width: 'min(560px, calc(100vw - 32px))',
          maxWidth: 560,
          borderRadius: 3,
          maxHeight: 'min(640px, calc(100vh - 64px))',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.28)',
          mt: { xs: 2, sm: 8 },
          alignSelf: 'flex-start',
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 26, flexShrink: 0 }} />
          <InputBase
            fullWidth
            autoFocus
            placeholder="Buscar pantallas y funcionalidades..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputRef={searchInputRef}
            sx={{
              fontSize: '1.05rem',
              fontWeight: 400,
              color: 'text.primary',
              '& input::placeholder': {
                color: 'text.secondary',
                opacity: 0.75,
              },
            }}
          />
          <KbdHint>Esc</KbdHint>
        </Box>

        <Box
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 1,
            py: 1,
            minHeight: 280,
            maxHeight: 460,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha('#000', 0.18),
              borderRadius: 3,
            },
          }}
        >
          {flatItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <SearchIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                No se encontraron resultados
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                Probá con otro término
              </Typography>
            </Box>
          ) : (
            flatItems.map((item, index) => {
              const showHeader = index === 0 || flatItems[index - 1].section !== item.section;
              const isSelected = index === selectedIndex;
              const accent =
                item.acceso.accent ||
                categoriaAccent[item.acceso.categoria] ||
                theme.palette.primary.main;
              const subtitle =
                item.section === 'Recientes'
                  ? `${item.acceso.categoria} · ${item.acceso.grupo}`
                  : item.acceso.grupo;

              return (
                <React.Fragment key={`${item.section}-${item.acceso.path}-${item.acceso.categoria}-${item.acceso.text}`}>
                  {showHeader && (
                    <Typography
                      sx={{
                        px: 1.25,
                        pt: index > 0 ? 1.5 : 0.75,
                        pb: 0.75,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                      }}
                    >
                      {item.section === 'Recientes' ? (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <HistoryIcon sx={{ fontSize: 12 }} />
                          Recientes
                        </Box>
                      ) : (
                        item.section
                      )}
                    </Typography>
                  )}
                  <Box
                    ref={(el: HTMLDivElement | null) => {
                      itemRefs.current[index] = el;
                    }}
                    onClick={() => handleAccesoClick(item.acceso)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.25,
                      py: 1,
                      mx: 0.25,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      transition: 'background-color 0.12s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: alpha(accent, 0.12),
                        color: accent,
                        '& .MuiSvgIcon-root': { fontSize: 20 },
                      }}
                    >
                      {item.acceso.icon}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          color: 'text.primary',
                          lineHeight: 1.25,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.acceso.text}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          lineHeight: 1.3,
                          mt: 0.15,
                        }}
                      >
                        {subtitle}
                      </Typography>
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })
          )}
        </Box>

        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            bgcolor: alpha('#000', 0.015),
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <KbdHint>
              <NorthIcon sx={{ fontSize: 11 }} />
            </KbdHint>
            <KbdHint>
              <SouthIcon sx={{ fontSize: 11 }} />
            </KbdHint>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
              navegar
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <KbdHint>Enter</KbdHint>
            <Typography variant="caption" color="text.secondary">
              abrir
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <KbdHint>Esc</KbdHint>
            <Typography variant="caption" color="text.secondary">
              cerrar
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BuscadorAccesosModal;
